import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { OperatorPlan, OperatorTenant } from '@forecastify/shared';
import { Icon, buttonClasses, cn } from '@forecastify/ui';
import { useI18n, useT, type MessageKey } from '../i18n';
import { tenantDeletePath } from '../routes';
import { formatMoney, formatNumber } from '../chrome/format';
import { BTN, DANGER_OUTLINE_BUTTON, EYEBROW } from '../chrome/tones';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ *Végleges törlés* — one act, four numbers, and a door rather than a verb. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE BUTTON DOES NOT DELETE. IT OPENS THE SCREEN WHERE DELETING HAPPENS.** There is no
 * suspend-first gate (S-16), so a live paying customer can be removed in one sitting; the
 * confirmation is the only guard, and it is a whole screen rather than a dialog.
 *
 * ⚠️ **The figures sit under the warning**, because they are what makes somebody reconsider before
 * clicking through at all.
 */
export function TenantDanger({
  tenant,
  plan,
}: {
  readonly tenant: OperatorTenant;
  readonly plan: OperatorPlan | undefined;
}): ReactNode {
  const t = useT();
  const { locale } = useI18n();

  const facts: readonly { readonly label: string; readonly value: string }[] = [
    { label: t('tenant.workspaces'), value: formatNumber(tenant.workspaceCount, locale) },
    { label: t('tenant.members'), value: formatNumber(tenant.memberCount, locale) },
    {
      label: t('danger.monthly'),
      /* ⚠️ `null` for every tenant that is not active — a revenue figure against a trial is a
         number nobody owes. A dash says so; a zero would be a claim. */
      value:
        tenant.monthlyValue === null || plan === undefined
          ? '–'
          : formatMoney(tenant.monthlyValue, plan.currency, locale),
    },
    { label: t('danger.status'), value: t(STATUS_LABEL[tenant.status]) },
  ];

  return (
    <section
      aria-label={t('danger.title')}
      className="border-destructive/50 flex flex-col gap-4 rounded-lg border p-5"
    >
      <div className="flex items-start gap-3">
        <span className="bg-destructive/10 text-destructive grid size-8 shrink-0 place-items-center rounded-md">
          <Icon name="trash" size={15} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[14px] font-medium">{t('danger.title')}</h3>
            <span className={cn('text-destructive', EYEBROW)}>{t('danger.tier')}</span>
          </div>
          <p className="text-muted-foreground max-w-[60ch] text-[12.5px] leading-[1.55] text-pretty">
            {t('danger.body')}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 border-t pt-3 text-[12.5px]">
        {facts.map((fact) => (
          <span key={fact.label}>
            <span className="text-muted-foreground">{fact.label}</span>{' '}
            <span className="font-medium tabular-nums">{fact.value}</span>
          </span>
        ))}
      </div>

      {/* ⚠️ A LINK, not a button with a navigate: the deletion screen is a real address. */}
      <Link
        to={tenantDeletePath(tenant.id)}
        className={buttonClasses(
          'outline',
          'default',
          cn(BTN, DANGER_OUTLINE_BUTTON, 'self-start'),
        )}
      >
        {t('danger.prepare')}
      </Link>
    </section>
  );
}

const STATUS_LABEL: Readonly<Record<OperatorTenant['status'], MessageKey>> = {
  trial: 'status.trial',
  active: 'status.active',
  suspended: 'status.suspended',
  awaiting_signup: 'status.awaitingSignup',
};
